import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Clock, CalendarDays, FolderKanban } from 'lucide-react';
import { TimesheetCalendar } from '../components/TimesheetCalendar';
import api from '../lib/api';
import { toast } from 'sonner';

export default function TimeSheetsTeam() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [detailWorker, setDetailWorker] = useState(null);
  const [workerProjects, setWorkerProjects] = useState([]);

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  const fetchData = async () => {
    try {
      const res = await api.get(`/timesheets/team?month=${monthStr}`);
      setData(res.data);
      if (detailWorker) {
        const updated = res.data.team.find(t => t.id === detailWorker.id);
        if (updated) setDetailWorker(updated);
      }
    } catch {
      toast.error('Kon team time sheets niet laden');
    }
  };
  useEffect(() => { fetchData(); }, [monthStr]);

  const navMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const maxWeekHours = Math.max(45, ...(data?.team || []).flatMap(w => w.weeks.map(x => x.hours)));

  useEffect(() => {
    if (detailWorker?.id) {
      api.get(`/timesheets/projects?worker_id=${detailWorker.id}`).then(r => setWorkerProjects(r.data || [])).catch(() => setWorkerProjects([]));
    }
  }, [detailWorker?.id]);

  const adminSave = async (date, start_time, end_time, projectRows) => {
    try {
      const res = await api.post(`/timesheets/admin/${detailWorker.id}`, { date, start_time, end_time, projects: projectRows || [] });
      toast.success(`Opgeslagen — ${res.data.hours}u`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon niet opslaan');
      throw err;
    }
  };
  const adminDelete = async (date) => {
    try {
      await api.delete(`/timesheets/admin/${detailWorker.id}/${date}`);
      toast.success('Werkdag verwijderd');
      fetchData();
    } catch {
      toast.error('Kon niet verwijderen');
    }
  };

  return (
    <div className="space-y-5" data-testid="timesheets-team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Time Sheets Team</h1>
          <p className="text-muted-foreground text-sm">Gewerkte uren van je Install/Clean medewerkers (1u pauze per dag afgetrokken)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navMonth(-1)} data-testid="team-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-medium w-36 text-center capitalize">
            {new Date(year, month - 1).toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="outline" size="icon" onClick={() => navMonth(1)} data-testid="team-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {(data?.project_totals || []).length > 0 && (
        <Card data-testid="team-project-totals">
          <CardContent className="pt-5">
            <p className="font-semibold mb-3 flex items-center gap-2"><FolderKanban className="w-4 h-4 text-blue-500" /> Uren per project deze maand</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
              {data.project_totals.map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate text-muted-foreground">{p.label}</span>
                  <span className="font-semibold flex-shrink-0">{p.hours}u</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data?.team || []).map(w => (
          <Card key={w.id} data-testid={`team-worker-${w.id}`}>
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{w.first_name} {w.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{w.email}</p>
                </div>
                <Badge variant="outline" className="capitalize flex-shrink-0">{w.role === 'cleaner' ? 'Install/Clean' : w.role}</Badge>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-orange-500">{w.total_hours}u</p>
                  <p className="text-xs text-muted-foreground">{w.days_worked} werkdagen deze maand</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setDetailWorker(w)} data-testid={`team-open-calendar-${w.id}`}>
                  <CalendarDays className="w-4 h-4 mr-1.5" /> Kalender
                </Button>
              </div>
              <div className="space-y-1.5">
                {w.weeks.length === 0 && <p className="text-xs text-muted-foreground">Nog geen uren ingevuld.</p>}
                {w.weeks.map(wk => (
                  <div key={wk.week} className="flex items-center gap-2 text-xs">
                    <span className="w-14 text-muted-foreground flex-shrink-0">Week {wk.week}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.min(100, (wk.hours / maxWeekHours) * 100)}%` }} />
                    </div>
                    <span className="w-12 text-right font-medium flex-shrink-0">{wk.hours}u</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {data && data.team.length === 0 && (
          <p className="text-muted-foreground">Geen Install/Clean medewerkers gevonden.</p>
        )}
      </div>

      <Dialog open={!!detailWorker} onOpenChange={(o) => !o && setDetailWorker(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              {detailWorker?.first_name} {detailWorker?.last_name} — {new Date(year, month - 1).toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })} ({detailWorker?.total_hours}u)
            </DialogTitle>
          </DialogHeader>
          {detailWorker && (
            <TimesheetCalendar
              year={year}
              month={month}
              entries={Object.fromEntries(detailWorker.entries.map(e => [e.date, e]))}
              canEdit={() => true}
              onSave={adminSave}
              onDelete={adminDelete}
              projects={workerProjects}
            />
          )}
          <p className="text-xs text-muted-foreground">Als admin kan je alle dagen corrigeren.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
